import type { UserSummaryDTO } from "@zplanem/responses"
import type { Locale } from "@zplanem/translations"

/**
 * Up to 2 letters identifying the user: first letter of first name + first
 * letter of last name, falling back to the first letter of whatever's present
 * (firstName-only → "J"), and finally the first letter of the email when no
 * names are set.
 */
export function getInitials(u: UserSummaryDTO | null | undefined): string {
	if (!u) return "?"
	const f = (u.firstName ?? "").trim()
	const l = (u.lastName ?? "").trim()
	if (f && l) return (f[0] + l[0]).toUpperCase()
	if (f) return f.slice(0, 1).toUpperCase()
	if (l) return l.slice(0, 1).toUpperCase()
	const e = (u.email ?? "").trim()
	return e ? e.slice(0, 1).toUpperCase() : "?"
}

/**
 * Full display name. Falls back to email when neither first nor last name
 * is set (matches how the share modal renders unknown invitees).
 */
export function getDisplayName(u: UserSummaryDTO | null | undefined): string {
	if (!u) return "?"
	const f = (u.firstName ?? "").trim()
	const l = (u.lastName ?? "").trim()
	if (f && l) return `${f} ${l}`
	if (f) return f
	if (l) return l
	return u.email ?? "?"
}

/** Short clock time like "14:30" for the chat row (current locale, 24h). */
export function formatShortTime(iso: string, locale: Locale): string {
	const d = new Date(iso)
	return new Intl.DateTimeFormat(localeToBcp47(locale), {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(d)
}

/** Full date + time for hover tooltips. */
export function formatFullDateTime(iso: string, locale: Locale): string {
	const d = new Date(iso)
	return new Intl.DateTimeFormat(localeToBcp47(locale), {
		dateStyle: "medium",
		timeStyle: "medium",
	}).format(d)
}

function localeToBcp47(locale: Locale): string {
	switch (locale) {
		case "pl":
			return "pl-PL"
		case "uk":
			return "uk-UA"
		default:
			return "en-US"
	}
}
